import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { classifyCart, upgradeMode } from '@theme/unbridled-cart-logic';

const routesRoot = window.Shopify?.routes?.root || '/';
const cartJsonUrl = `${routesRoot}cart.js`;
const cartAddUrl = `${routesRoot}cart/add.js`;
const cartChangeUrl = `${routesRoot}cart/change.js`;

const sectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .slice(0, 5);

const withSections = (payload) => {
  const sections = sectionIds();
  if (sections.length === 0) return payload;

  return {
    ...payload,
    sections,
    sections_url: `${window.location.pathname}${window.location.search}`,
  };
};

async function parseResponse(response) {
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'The cart could not be updated.');
  }
  return data;
}

async function getCart() {
  return parseResponse(await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } }));
}

async function changeLine(key, quantity, includeSections = true) {
  const body = includeSections ? withSections({ id: key, quantity }) : { id: key, quantity };
  return parseResponse(
    await fetch(cartChangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  );
}

async function addVariant(variantId, includeSections = true) {
  const body = includeSections
    ? withSections({ items: [{ id: Number(variantId), quantity: 1 }] })
    : { items: [{ id: Number(variantId), quantity: 1 }] };

  return parseResponse(
    await fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  );
}

function dispatchCartUpdate(cart, sourceId, sections = {}) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, sourceId, {
      source: 'unbridled-experience',
      itemCount: cart.item_count,
      sections,
    })
  );
}

function setButtonLoading(button, loading) {
  button.disabled = loading || button.dataset.originalDisabled === 'true';
  button.setAttribute('aria-busy', String(loading));
  if (loading) {
    button.dataset.originalText = button.textContent.trim();
    button.textContent = 'Adding experience…';
  } else if (button.dataset.originalText) {
    button.textContent = button.dataset.originalText;
  }
}

async function handleExperienceAdd(button) {
  if (button.getAttribute('aria-busy') === 'true') return;

  button.dataset.originalDisabled = String(button.disabled);
  const root = button.closest('[data-experience-root]');
  const status = root?.querySelector('[data-experience-status]');
  setButtonLoading(button, true);
  if (status) status.textContent = '';

  try {
    const response = await addVariant(button.dataset.variantId);
    const cart = await getCart();
    dispatchCartUpdate(cart, button.dataset.variantId || 'experience-add', response.sections);
    if (status) status.textContent = `${button.dataset.originalText} added to your cart.`;
  } catch (error) {
    if (status) status.textContent = error instanceof Error ? error.message : 'The experience could not be added.';
  } finally {
    setButtonLoading(button, false);
  }
}

if (!window.__unbridledExperienceAddListener) {
  window.__unbridledExperienceAddListener = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-experience-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    event.preventDefault();
    handleExperienceAdd(button);
  });
}

class UnbridledExperienceGallery extends HTMLElement {
  #abortController = new AbortController();
  #currentIndex = 0;
  #snapRestoreTimer = 0;

  connectedCallback() {
    const { signal } = this.#abortController;
    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.#goTo(this.#currentIndex - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#goTo(this.#currentIndex + 1), { signal });
    this.querySelector('[data-gallery-track]')?.addEventListener('scroll', this.#updateFromScroll, { passive: true, signal });
    this.#updateControls();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#snapRestoreTimer);
    this.#abortController.abort();
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]'));
  }

  #goTo(index) {
    const slides = this.#slides;
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    window.clearTimeout(this.#snapRestoreTimer);
    track.removeEventListener('scrollend', this.#restoreSnap);
    track.style.scrollSnapType = 'none';
    track.addEventListener('scrollend', this.#restoreSnap, { once: true });
    track.scrollTo({
      left: nextIndex * track.clientWidth,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    this.#snapRestoreTimer = window.setTimeout(this.#restoreSnap, 500);
    this.#currentIndex = nextIndex;
    this.#updateControls();
  }

  #restoreSnap = () => {
    const track = this.querySelector('[data-gallery-track]');
    window.clearTimeout(this.#snapRestoreTimer);
    this.#snapRestoreTimer = 0;
    if (track) track.style.scrollSnapType = '';
  };

  #updateFromScroll = () => {
    const track = this.querySelector('[data-gallery-track]');
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index === this.#currentIndex) return;
    this.#currentIndex = index;
    this.#updateControls();
  };

  #updateControls() {
    const slides = this.#slides;
    const previous = this.querySelector('[data-gallery-previous]');
    const next = this.querySelector('[data-gallery-next]');
    const current = this.querySelector('[data-gallery-current]');
    if (previous) previous.disabled = this.#currentIndex === 0;
    if (next) next.disabled = this.#currentIndex >= slides.length - 1;
    if (current) current.textContent = String(this.#currentIndex + 1).padStart(2, '0');
  }
}

class UnbridledCartUpsell extends HTMLElement {
  #busy = false;
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.querySelector('[data-upgrade-submit]')?.addEventListener('click', this.#upgradeOrder, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshEligibility, { signal });
    queueMicrotask(this.#refreshEligibility);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get ids() {
    return {
      bourbonProductId: this.dataset.bourbonProductId,
      boxProductId: this.dataset.boxProductId,
      collectionProductId: this.dataset.collectionProductId,
    };
  }

  #refreshEligibility = async () => {
    try {
      const cart = await getCart();
      const mode = upgradeMode(cart, this.ids);
      this.#applyMode(mode);
      this.hidden = mode === null;
    } catch {
      this.hidden = true;
    }
  };

  #applyMode(mode) {
    this.dataset.upgradeMode = mode || '';

    const boxOption = this.querySelector('[data-upgrade-option="box"]');
    if (boxOption) boxOption.hidden = mode === 'box';

    this.querySelectorAll('[data-upgrade-copy]').forEach((copy) => {
      copy.hidden = copy.dataset.upgradeCopy !== mode;
    });

    if (mode === 'box') {
      const collectionInput = this.querySelector('input[name="unbridled-cart-upgrade"][value="collection"]');
      if (collectionInput) collectionInput.checked = true;
    }

    const submit = this.querySelector('[data-upgrade-submit]');
    if (submit && !this.#busy) {
      const collectionInput = this.querySelector('input[name="unbridled-cart-upgrade"][value="collection"]');
      submit.disabled = mode === 'box'
        ? Boolean(collectionInput?.disabled)
        : Array.from(this.querySelectorAll('input[name="unbridled-cart-upgrade"]')).every((input) => input.disabled);
      submit.textContent = mode === 'box' ? 'Upgrade to Experience Collection' : 'Upgrade My Order';
    }
  }

  #selectedUpgrade(mode) {
    if (mode === 'box') {
      return {
        productId: this.dataset.collectionProductId,
        variantId: this.dataset.collectionVariantId,
      };
    }

    const value = this.querySelector('input[name="unbridled-cart-upgrade"]:checked')?.value;
    if (value === 'collection') {
      return {
        productId: this.dataset.collectionProductId,
        variantId: this.dataset.collectionVariantId,
      };
    }
    return {
      productId: this.dataset.boxProductId,
      variantId: this.dataset.boxVariantId,
    };
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.querySelectorAll('button, input').forEach((control) => {
      if (busy) control.dataset.originalDisabled = String(control.disabled);
      control.disabled = busy || control.dataset.originalDisabled === 'true';
    });
    const submit = this.querySelector('[data-upgrade-submit]');
    if (submit) {
      if (busy) submit.dataset.originalText = submit.textContent.trim();
      submit.textContent = busy ? 'Upgrading order…' : submit.dataset.originalText || 'Upgrade My Order';
      submit.setAttribute('aria-busy', String(busy));
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-upgrade-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  async #rollback(originalCart, upgradeProductId, originalLine) {
    let currentCart = await getCart();
    let current = classifyCart(currentCart, this.ids);
    const originalLineStillExists = current.topLevelItems.some(
      (item) => String(item.product_id) === String(originalLine.product_id)
    );

    if (!originalLineStillExists) {
      const originalVariantId = originalLine.variant_id || (
        String(originalLine.product_id) === String(this.dataset.boxProductId)
          ? this.dataset.boxVariantId
          : this.dataset.bourbonVariantId
      );
      await addVariant(originalVariantId, false);
      currentCart = await getCart();
      current = classifyCart(currentCart, this.ids);
    }

    const upgradeLines = current.topLevelItems.filter(
      (item) => String(item.product_id) === String(upgradeProductId)
    );

    for (const item of upgradeLines) {
      await changeLine(item.key, 0, false);
    }

    const restored = await getCart();
    if (upgradeMode(restored, this.ids) !== upgradeMode(originalCart, this.ids)) {
      throw new Error('Your original selection could not be restored automatically. Please review your cart.');
    }
  }

  #upgradeOrder = async () => {
    if (this.#busy) return;
    const errorMessage = 'We could not upgrade your order. Your original selection remains in the cart.';
    let selection;
    let originalLine;
    let originalCart;
    let addResponse;
    let addAttempted = false;
    let transactionComplete = false;

    this.#showError('');
    this.#setBusy(true);

    try {
      originalCart = await getCart();
      const mode = upgradeMode(originalCart, this.ids);
      if (mode === null) {
        throw new Error('Your cart changed. Review it before trying the upgrade again.');
      }

      const classification = classifyCart(originalCart, this.ids);
      originalLine = mode === 'box' ? classification.boxLines[0] : classification.standaloneBourbonLines[0];
      selection = this.#selectedUpgrade(mode);
      addAttempted = true;
      addResponse = await addVariant(selection.variantId);
      const cartWithBundle = await getCart();
      const addedBundleLine = classifyCart(cartWithBundle, this.ids).topLevelItems.find(
        (item) => String(item.product_id) === String(selection.productId)
      );
      if (!addedBundleLine) throw new Error('Shopify did not return the bundle parent line.');

      const finalCart = await changeLine(originalLine.key, 0);
      transactionComplete = true;
      dispatchCartUpdate(finalCart, selection.variantId || 'experience-upgrade', finalCart.sections || addResponse.sections);
      this.hidden = true;
    } catch (error) {
      if (originalCart && originalLine && selection && addAttempted && !transactionComplete) {
        try {
          await this.#rollback(originalCart, selection.productId, originalLine);
        } catch (rollbackError) {
          this.#showError(rollbackError instanceof Error ? rollbackError.message : errorMessage);
          this.#setBusy(false);
          return;
        }
      }
      this.#showError(error instanceof Error ? error.message : errorMessage);
    } finally {
      this.#setBusy(false);
    }
  };
}

if (!customElements.get('unbridled-experience-gallery')) {
  customElements.define('unbridled-experience-gallery', UnbridledExperienceGallery);
}

if (!customElements.get('unbridled-cart-upsell')) {
  customElements.define('unbridled-cart-upsell', UnbridledCartUpsell);
}
